/**
 * This module contains common functions for redux (actions, sagas, reducers) related things
 */
import React from 'react';
import dayjs from 'dayjs';
import Axios from 'axios';
import produce from 'immer';
import classNames from 'classnames';
import { Storage, Auth } from 'aws-amplify';
import { createAction } from '@reduxjs/toolkit';
import { message as antMessage, Tooltip } from 'antd';
import isEqual from 'lodash/isEqual';
import lodashMerge from 'lodash/merge'; // lodash/get is not getting nested keys
import setNestedValue from 'lodash/set';
import isNil from 'lodash/isNil';

import {
  SIMPLE_PHONE_NUMBER_REGEX,
  FORMATTED_PHONE_NUMBER_REGEX,
  PHONE_NUMBER_EXT_REGEX,
  DATE_FORMAT,
  TIME_FORMAT,
  CLIENT_DATE_TIME_FORMAT,
  ROLES,
  EMDASH,
} from 'constants/index';
import providerService from 'services/providers.service';
import userService from 'services/users.service';
import { actions as centerActions } from 'features/add-center';
import { Home, InHome, FamilyGroup, SchoolChildCare, ReligiousCareFacility } from 'utils/icons';
import ColumnTitle from 'components/ColumnTitle';
import BigNumber from 'bignumber.js';

class CancelToken {
  constructor(initialValue) {
    this.source = initialValue;
  }
  getSource() {
    return this.source;
  }
  setSource(value) {
    this.source = value;
  }
  cancel() {
    this.source.cancel();
  }
}

export const cancelSource = new CancelToken(Axios.CancelToken.source());

export const sleep = (time, resolve = true, message) =>
  new Promise((res, rej) => {
    setTimeout(resolve ? res : () => rej({ message: message || 'Error' }), time);
  });

export const getValue = (data, key, defaultValue = undefined) =>
  (Array.isArray(key) ? key : [key]).reduce((p, c) => (p && p[c]) || defaultValue, data);

export const dayjsToValues = (dayjsObj) => {
  return {
    day: dayjsObj.date(),
    month: dayjsObj.month() + 1,
    year: dayjsObj.year(),
  };
};

export const valuesToDayjs = (values) => {
  if (!values) return null;
  return dayjs(new Date(values.year, values.month - 1, values.day));
};

export const dateRangeToString = (dateRange, format = 'MMM DD') => {
  const { from, to } = dateRange || { from: null, to: null };
  const start = valuesToDayjs(from);
  const end = valuesToDayjs(to);
  let date = start ? start.format(format) : '';
  if (start && end) {
    date += ` - ${end.format(format)}`;
  }
  return date;
};
export const stringToDateRange = (input, year = dayjs().year()) => {
  if (input.indexOf(' - ') === -1) return { from: null, to: null };
  const [from, to] = input.split(' - ');
  return {
    from: dayjsToValues(dayjs(`${from} ${year}`)),
    to: dayjsToValues(dayjs(`${to} ${year}`)),
  };
};

export const parseQueryParams = (querystring) => {
  // parse query string
  const params = new URLSearchParams(querystring);

  const obj = {};

  // iterate over all keys
  for (const key of params.keys()) {
    let value;
    if (params.getAll(key).length > 1) {
      value = params.getAll(key);
    } else {
      value = params.get(key);
    }
    if (value !== undefined) obj[key] = value;
  }
  const dateRange = {};
  if (obj.from) {
    dateRange.from = dayjsToValues(dayjs(obj.from));
  }
  if (obj.to) {
    dateRange.to = dayjsToValues(dayjs(obj.to));
  }
  if (obj.from || obj.to) {
    obj.dateRange = dateRange;
  }
  if (obj.distanceInMiles) {
    obj.distanceInMiles = Number(obj.distanceInMiles);
  } else {
    delete obj.distanceInMiles;
  }
  if (obj.ageInMonths) {
    obj.ageInMonths = Number(obj.ageInMonths);
  } else {
    delete obj.ageInMonths;
  }
  if (obj.facilityTypes) {
    obj.facilityTypes = obj.facilityTypes.split(',');
  } else {
    delete obj.facilityTypes;
  }
  return obj;
};
export const paramsToQueryString = (values, isPublicSearch) => {
  const { dateRange, facilityTypes } = values;
  const params = {
    ...values,
  };
  if (dateRange && dateRange.from) {
    params.from = valuesToDayjs(dateRange.from).format('YYYY-MM-DD');
  }
  if (dateRange && dateRange.to) {
    params.to = valuesToDayjs(dateRange.to).format('YYYY-MM-DD');
  }
  delete params.dateRange;
  if (facilityTypes && facilityTypes.length > 0) {
    params.facilityTypes = facilityTypes.join(',');
  }
  Object.keys(params).forEach((key) => {
    if (params[key] === undefined || params[key] === '') {
      delete params[key];
    }
  });
  if (params.location) {
    params.placeId = params.location.place_id || params.location.placeId || params.placeId;
  }
  if (!params.ageInMonths || params.ageInMonths === 0) {
    delete params.ageInMonths;
  }
  if (!params.distanceInMiles || params.distanceInMiles === 0) {
    delete params.distanceInMiles;
  }
  if (!params.facilityTypes || params.facilityTypes?.length === 0) {
    delete params.facilityTypes;
  }
  delete params.location;
  return new URLSearchParams(params).toString();
};

export const updateQueryParams = (history, key, value) => {
  const { location } = history;
  const search = location.search;
  const pathname = location.pathname;
  const params = new URLSearchParams(search);
  if (key === 'amenities' && (!value || value?.length === 0)) {
    params.delete(key);
  } else if (key === 'price' && value[0] === 0 && value[1] === 0) {
    params.delete(key);
  } else if (key === 'languages' && (!value || value.length === 0)) {
    params.delete(key);
  } else if (key === 'distanceInMiles' && Number(value) === 0) {
    params.delete(key);
  } else if (key === 'ageInMonths' && Number(value) === 0) {
    params.delete(key);
  } else if (key === 'facilityType' && (!value || value.length === 0)) {
    params.delete(key);
  } else if (key === 'facilityTypes' && !value) {
    params.delete(key);
  } else {
    params.set(key, value);
  }
  history.push({
    pathname,
    search: params.toString(),
  });
};

export const parseRange = (value, formatter) => {
  if (!value) return undefined;
  if (value.indexOf(',') > -1) {
    return value.split(',').map(formatter);
  } else {
    return [0, 0];
  }
};

export const concatLatLng = (point) => {
  if (!point) return '';
  return `${point.lat},${point.lng}`;
};
export const getCompletedAddress = (address) => {
  if (!address) return '';
  const addressComponents = [address?.street?.trim?.(), address?.city, address?.state, address?.postalCode];
  if (address?.country !== 'US') {
    addressComponents.push(address.country);
  }
  let fullAddress = addressComponents.filter(Boolean).join(', ');
  return fullAddress.trim();
};

export const message = {
  ...['success', 'error', 'warning', 'info'].reduce((funcs, name) => {
    funcs[name] = (content, options = {}) => {
      const { ariaLive = 'assertive', duration = 1.5, style = {} } = options;
      antMessage[name](
        <div style={{ display: 'inline-block', ...style }} aria-live={ariaLive}>
          {content}
        </div>,
        duration,
      );
    };

    return funcs;
  }, {}),
};

export const passwordValidator = () => ({
  validator(rule, value) {
    if (!value) return Promise.resolve();
    let errors = [];
    if (!/[a-z]+/.test(value)) {
      errors.push('1 lower case letter');
    }
    if (!/[A-Z]+/.test(value)) {
      errors.push('1 upper case letter');
    }
    if (!/\d+/.test(value)) {
      errors.push('1 numeric');
    }
    if (!/[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(value)) {
      errors.push('1 special character');
    }
    if (value.length < 8) {
      errors.push('8 characters');
    }
    if (errors.length === 0) {
      return Promise.resolve();
    }
    const error = errors.reduce((err, curr, index) => {
      if (index + 1 === errors.length) {
        return `${err} and ${curr}`;
      }
      return `${err}, ${curr}`;
    });
    return Promise.reject(`Password must have ${error}.`);
  },
});

export const breakpointsContext = React.createContext({});
export const LayoutContext = React.createContext({});
export const SnapshotContext = React.createContext({});
export const ScrollContext = React.createContext([false, () => {}, false]);

export const frequencyOptions = [
  {
    title: 'Week',
    id: 'WEEKLY',
  },
  {
    title: 'Month',
    id: 'MONTHLY',
  },
  {
    title: 'Year',
    id: 'YEARLY',
  },
  {
    title: 'Other',
    id: 'OTHER',
  },
];
export const frequencyOptionsMap = {
  MONTHLY: 'Month',
  WEEKLY: 'Week',
  YEARLY: 'Year',
  OTHER: 'Other',
};
export const frequencyWeights = {
  WEEKLY: 7,
  MONTHLY: 30,
  YEARLY: 365,
  ANNUAL: 365,
  OTHER: 0,
};

export const registrationFrequencyOptions = [
  {
    title: 'Annual',
    id: 'ANNUAL',
  },
  {
    title: 'One-Time',
    id: 'ONE_TIME',
  },
  {
    title: 'Other',
    id: 'OTHER',
  },
];

export const ageGroupOptions = [
  ...Array.from({ length: 11 }, (_, i) => i + 1).map((i) => ({
    title: `${i} ${i > 1 ? 'Months' : 'Month'}`,
    id: i,
  })),
  ...Array.from({ length: 13 }, (_, i) => i + 1).map((i) => ({
    title: `${i} ${i > 1 ? 'Years' : 'Year'}`,
    id: i * 12,
  })),
];

export const numberValidator = async (rule, value) => {
  if (value === undefined || value === '') return;
  if (Number.isNaN(Number(value))) {
    throw new Error('Enter numbers only.');
  }
  if (value < 0) {
    throw new Error("Value can't be negative number.");
  }
};
export const getMinMaxNumberValidator =
  ({ min, max, minMessage, maxMessage }) =>
  async (rule, value) => {
    if (value === undefined || value === '') return;
    if (Number.isNaN(Number(value))) {
      throw new Error('Not a valid number.');
    }
    if (min && min > value) {
      throw new Error(minMessage || 'Value is too small.');
    }
    if (min && max < value) {
      throw new Error(maxMessage || 'Value is too large.');
    }
  };
export const getNumericValidator = (message) => async (rule, value) => {
  if (value === undefined || value === '') return;
  if (!/^\d+$/.test(value) || value < 0) {
    throw new Error(message);
  }
};

export const getStringValidator = (message) => async (rule, value) => {
  if (/^\d+$/.test(value)) {
    throw new Error(message);
  }
};

export const validateDates = (getFieldValue, startField, endField, errorMessage) => {
  return () => {
    let startDate = getFieldValue(startField);
    let endDate = getFieldValue(endField);

    // Convert to dayjs objects if they're not already
    startDate = startDate && (dayjs.isDayjs(startDate) ? startDate : dayjs(startDate));
    endDate = endDate && (dayjs.isDayjs(endDate) ? endDate : dayjs(endDate));

    if (startDate && endDate) {
      if (startDate.isAfter(endDate) || endDate.isBefore(startDate)) {
        return Promise.reject(new Error(errorMessage));
      }
    }
    return Promise.resolve();
  };
};

export const getPastDateValidator = (message = 'Date should be in paste.') => ({
  validator: async (rule, value) => {
    if (!value) return;
    if (dayjs(value).isAfter(dayjs().endOf('d'))) {
      throw new Error(message);
    }
  },
});

export const getDisabledDateValidator =
  (isDisabledDate, message = 'Date is not allowed.') =>
  (form) => ({
    validator: async (rule, value) => {
      if (!value) return;
      if (isDisabledDate(value, form)) {
        throw new Error(message);
      }
    },
  });

export class S3 {
  // static async get(key, { bucket, ...rest } = {}) {
  //   if (!bucket) {
  //     return await Storage.get(key, { ...rest, bucket: 'greatchildcare-artefacts233546-dev' });
  //   }
  //   return await Storage.get(key, { bucket, ...rest });
  // }
  static async get(key, { ...rest } = {}) {
    return await Storage.get(key, { ...rest });
  }
}

export const getTextSorter = (key) => (b, a) => {
  let aValue = getValue(a, key);
  let bValue = getValue(b, key);
  if (aValue && bValue) {
    aValue = aValue?.toLowerCase?.();
    bValue = bValue?.toUpperCase?.();
    return bValue?.localeCompare ? bValue?.localeCompare(aValue) : 0;
  }
  if (aValue && !bValue) return -1;
  if (!aValue && bValue) return 1;

  return 0;
};
export const getBooleanSorter = (key) => (b, a) => {
  const aValue = getValue(a, key);
  const bValue = getValue(b, key);
  if (aValue && !bValue) return -1;
  if (!aValue && bValue) return 1;
  return 0;
};

export const getSorter = (getter) => (a, b) => {
  const aValue = getter(a);
  const bValue = getter(b);
  if (aValue && bValue) return bValue?.localeCompare(aValue);
  if (aValue && !bValue) return -1;
  if (!aValue && bValue) return 1;
  return 0;
};
export const getNumberSorter = (key) => (a, b) => {
  const aValue = getValue(a, key);
  const bValue = getValue(b, key);
  return aValue - bValue;
};

export const getDateSorter =
  (key, nullAtEnd = false, sort = 'asc') =>
  (a, b) => {
    const aValue = getValue(a, key);
    const bValue = getValue(b, key);
    // console.log('🚀 ~ file: index.jsx:468 ~ aValue, bValue:', aValue, bValue);
    if (aValue === bValue) return 0;
    if (aValue && bValue) {
      if (sort === 'acs') {
        return dayjs(aValue).isAfter(dayjs(bValue)) ? 1 : -1;
      }
      return dayjs(aValue).isAfter(dayjs(bValue)) ? -1 : 1;
    }
    if (aValue && !bValue) return nullAtEnd ? -1 : 1;
    if (!aValue && bValue) return nullAtEnd ? 1 : -1;
  };

export const dependencyRequiredValidator =
  (dependencyKey, message, when = (value) => !!value) =>
  ({ getFieldValue }) => {
    return {
      validator(_, value) {
        if (when(getFieldValue(dependencyKey)) && !value) {
          return Promise.reject(message);
        }

        return Promise.resolve();
      },
    };
  };

export function compareValues(formValue, currentSavedValue) {
  if (!isNil(currentSavedValue) && formValue !== currentSavedValue) return 'error';
}
export function isSame(prev, curr) {
  if (prev === curr) return true;
  if (
    (prev === null && curr === undefined) ||
    (prev === undefined && curr === null) ||
    (prev === undefined && curr === '')
  )
    return true;
  if ((prev === undefined || prev === null) && curr === false) return true;
  return false;
}
export function isSameDate(prev, curr) {
  if (prev === curr) return true;
  if (prev === undefined && curr === null) return true;
  if (typeof prev === 'string' && dayjs.isDayjs(curr)) {
    return prev === curr.format(DATE_FORMAT[2]);
  }
  return false;
}

export function copy(obj, keys) {
  return keys.reduce((prev, key) => {
    prev[key] = getValue(obj, key);
    return prev;
  }, {});
}
export function changeRequestClassName(keys, snapshotCollection) {
  return (record, index) => {
    const curr = copy(record, keys);
    const isSame = snapshotCollection?.some((row) => {
      return isEqual(curr, copy(row, keys));
    });

    return classNames('h-10 !border-b  !border-white', {
      'bg-blue-700 bg-opacity-5': isSame,
      'bg-orange-100': !isSame && index % 2 === 0,
      'bg-orange-100 bg-opacity-50': !isSame && index % 2 !== 0,
    });
  };
}
// TODO: NEED TO REVISIT THE LOGIC FOR A COMMON COMPONENT.
// export function compareValues(formValue, currentSavedValue, status) {
//   if (!['INCOMPLETE', 'SUBMITTED'].includes(status) && formValue !== currentSavedValue) return 'error';
// }

export const dependencyNumberValidator =
  (dependencyKey) =>
  ({ getFieldValue }) => {
    return {
      validator: async (_, value) => {
        if (getFieldValue(dependencyKey)) {
          return numberValidator(_, value);
        }
        return Promise.resolve();
      },
    };
  };

export const getBooleanRequiredValidator = (message) => () => {
  return {
    validator(_, value) {
      if (!value) {
        return Promise.reject(message);
      }
      return Promise.resolve();
    },
  };
};
export const getPhoneNumberValidator = (message) => () => {
  return {
    validator(_, value) {
      if (!value) {
        return Promise.resolve();
      }

      if (isPhoneNumberValid(value)) {
        return Promise.resolve();
      }

      return Promise.reject(message);
    },
  };
};
export const isPhoneNumberValid = (value) => {
  return (
    (value?.length === 14 && FORMATTED_PHONE_NUMBER_REGEX.test(value)) ||
    (value?.length === 12 && SIMPLE_PHONE_NUMBER_REGEX.test(value))
  );
};
export const getPhoneEXTValidator = (message) => () => {
  return {
    validator(_, value) {
      if (!value) {
        return Promise.resolve();
      }

      if (value && PHONE_NUMBER_EXT_REGEX.test(value)) {
        return Promise.resolve();
      }
      return Promise.reject(message);
    },
  };
};

export const getMilitaryEmailValidator = (message) => () => {
  return {
    validator(_, value) {
      if (!value) {
        return Promise.resolve();
      }
      if (!/\.mil$/.test(value)) {
        return Promise.reject(message);
      }
      return Promise.resolve();
    },
  };
};

export const getCognitoUserInfo = async (usr) => {
  const [cognitoUser, session, dbUser] = await Promise.all([
    Auth.currentUserInfo(),
    Auth.userSession(usr),
    userService.getCurrentUser(),
  ]);
  const groups = session.accessToken.payload['cognito:groups'];
  const attrs = cognitoUser.attributes;
  let user = {
    ...dbUser,
    identityId: cognitoUser.id,
    username: cognitoUser.username,
    firstName: dbUser.firstName || attrs.given_name,
    middleName: '',
    lastName: dbUser.lastName || attrs.family_name,
    phone: attrs.phone_number,
    email: attrs.email,
    groups,
  };
  return user;
};

export const hasPermission = (userRoles, requiredRoles, operator = 'OR') => {
  if (typeof requiredRoles === 'string') {
    return userRoles?.indexOf(requiredRoles) > -1;
  }
  if (Array.isArray(requiredRoles)) {
    return requiredRoles.reduce((allowed, name) => {
      if (operator === 'AND') return allowed && userRoles?.indexOf(name) > -1;
      return allowed || userRoles?.indexOf(name) > -1;
    }, operator === 'AND');
  }
  return false;
};

export const getFormattedPhoneNumber = (value, extension) => {
  if (!value) return EMDASH;

  let formatted;
  if (SIMPLE_PHONE_NUMBER_REGEX.test(value)) {
    formatted = `(${value.slice(2, 5)}) ${value.slice(5, 8)}-${value.slice(8, 12)}`;
  } else {
    value = value.replace(/\D+/g, '');
    /* istanbul ignore else */
    if (/^\d{10}$/.test(value)) {
      formatted = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
    } else if (/^1\d{10}$/.test(value)) {
      /* istanbul ignore else */
      formatted = `(${value.slice(1, 4)}) ${value.slice(4, 7)}-${value.slice(7, 11)}`;
    } else {
      formatted = value;
    }
  }

  return extension ? `${formatted} ext. ${extension}` : formatted;
};

export const getGooglePlace = async (placeId) => {
  return new Promise((res, rej) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ placeId }, (results, status) => {
      if (status === 'OK' && results.length > 0) {
        res(results[0]);
      } else {
        rej({ message: 'Unable to find this place' });
      }
    });
  });
};
export const getPlaceLatLng = (place) => {
  return {
    lat: place.geometry.location.lat(),
    lng: place.geometry.location.lng(),
  };
};

export function keyCodeMatches(event, keysToMatch) {
  for (let i = 0; i < keysToMatch.length; i++) {
    if (keysToMatch[i] === event.which || keysToMatch[i] === event.key) {
      return true;
    }
  }
  return false;
}

// export const clone = lodashClone;
// export const jsonClone = (data) => JSON.parse(JSON.stringify(data));
export const deepClone = (obj) => {
  if (obj === null) return null;
  let clone = Object.assign({}, obj);
  Object.keys(clone).forEach(
    (key) => (clone[key] = typeof obj[key] === 'object' && !dayjs.isDayjs(obj[key]) ? deepClone(obj[key]) : obj[key]),
  );
  if (Array.isArray(obj)) {
    clone.length = obj.length;
    return Array.from(clone);
  }
  return clone;
};

export const clone = deepClone;

export const merge = (data1, data2) => lodashMerge(clone(data1), clone(data2));

export function createAsyncAction(prefix) {
  const action = createAction(`${prefix}/pending`);
  action.pending = createAction(`${prefix}/pending`);
  action.fulfilled = createAction(`${prefix}/fulfilled`);
  action.rejected = createAction(`${prefix}/rejected`);
  return action;
}

export const formatDates = (data, formatKeys = [], format = 'YYYY-MM-DD') => {
  if (!formatKeys || formatKeys?.length === 0) {
    return data;
  }
  return produce(data, (draft) => {
    formatKeys.forEach((key) => {
      const value = getValue(draft, key);
      if (dayjs.isDayjs(value)) {
        setNestedValue(draft, key, value.format(format));
      } else if (dayjs(value, DATE_FORMAT, 'es', true).isValid()) {
        setNestedValue(draft, key, dayjs(value).format(format));
      }
    });
  });
};

export const formatDate = (dateString, format = DATE_FORMAT[1], fallback = 'N/A') => {
  const date = dayjs(dateString);
  return dateString && date.isValid() ? date.format(format) : fallback;
};
export const formatDateTimeToLocal = (dateTimeString, format = CLIENT_DATE_TIME_FORMAT, fallback = 'N/A') => {
  const date = dayjs.utc(dateTimeString);
  return dateTimeString && date.isValid() ? date.local().format(format) : fallback;
};

export const formatTo24HourTime = (time, defaultValue = null) => {
  if (time) {
    return dayjs(time, TIME_FORMAT.HOURS_MINUTES_AM_PM).format(TIME_FORMAT.HOURS_MINUTES);
  }
  return defaultValue;
};

export const calculateTiming = (d) => {
  let months = 0,
    years = 0,
    days = 0,
    weeks = 0;
  while (d) {
    if (d >= 365) {
      years++;
      d -= 365;
    } else if (d >= 30) {
      months++;
      d -= 30;
    } else if (d >= 7) {
      weeks++;
      d -= 7;
    } else {
      days++;
      d--;
    }
  }
  return {
    years,
    months,
    weeks,
    days,
  };
};

const ICONS = {
  'Family/Group Child Care Home': FamilyGroup,
  'Child Care Center': Home,
  'School Child Care Facility': SchoolChildCare,
  'Religious Or Other Facility': ReligiousCareFacility,
  'In-Home Child Care': InHome,
};

export const CenterTypeInput = ({ centerType, title, className, iconClassName }) => {
  const Icon = ICONS[title] || Home;
  return (
    <div className={classNames('center-type rounded-full shadow-sm', className)} id={centerType}>
      <div className="center-box flex justify-center items-center" key={title}>
        <Icon className={classNames('w-40 h-40', iconClassName)} />
      </div>
    </div>
  );
};

export function htmlToText(html) {
  // Create a new div element
  var tempDivElement = document.createElement('div');

  // Set the HTML content with the given value
  tempDivElement.innerHTML = html;

  // Retrieve the text property of the element
  return tempDivElement.textContent || tempDivElement.innerText || '';
}

export const hoursRenderer = (day) => {
  const today = dayjs().startOf('day');
  const todayStr = today.format('YYYY-MM-DD');
  const start = dayjs(`${todayStr}T${day?.startTime}`);
  const end = dayjs(`${todayStr}T${day?.endTime}`);
  if (day?.startTime && day?.endTime) {
    if (end.diff(start, 'h') < 1) {
      return today.diff(start.subtract(1, 'day'), 'h') + end.diff(today, 'h');
    }
    return end.diff(start, 'h');
  } else {
    return 'N/A';
  }
};

export async function getCenterSnapshot(facilityId, dispatch) {
  try {
    let { careFacility: center } = await providerService.getCenterSnapshot(facilityId, 11 /* CR_PENDING */);
    const ageGroups = center.ageGroups || [];
    center.staff = Array.from(center.staff || []).map((item) => ({
      ...item,
      startDate: item?.person?.position?.startDate,
      endDate: item?.person?.position?.endDate,
    }));
    const credentialsData = center.staff.find((staff) => staff.efmDesignate) || {};
    const exp = Array.from(credentialsData.experiences || []);
    const trainings = Array.from(credentialsData.trainings || []);
    const educations = Array.from(credentialsData.educations || []).map((i) => ({
      ...i,
      completed: i.completed || false,
    }));
    const experiences = exp.filter((i) => i.experienceType);
    const otherExperiences = exp
      .filter((i) => !i.experienceType)
      .map((i) => ({
        ...i,
        value: i.experienceTitle,
        label: i.experienceTitle,
      }));
    const inServices = trainings.filter((i) => i.inService);
    const preServices = trainings.filter((i) => !i.inService);

    const hoursOfOperation = center?.hoursOfOperation?.map((item) => {
      const data = { ...item };
      if (item.openingTime) {
        data.openingTime = dayjs(item.openingTime, 'HH:mm');
      }
      if (item.closingTime) {
        data.closingTime = dayjs(item.closingTime, 'HH:mm');
      }
      return data;
    });

    const standardHours = hoursOfOperation?.filter((item) => item.type === 'REGULAR');
    const beforeSchoolCareHours = hoursOfOperation?.filter((item) => item.type === 'BEFORE_SCHOOL');
    const afterSchoolCareHours = hoursOfOperation?.filter((item) => item.type === 'AFTER_SCHOOL');
    const respiteCareHours = hoursOfOperation?.filter((item) => item.type === 'RESPITE');
    const beforeSchoolCare = beforeSchoolCareHours?.some((item) => item.openingTime && item.closingTime);
    const afterSchoolCare = afterSchoolCareHours?.some((item) => item.openingTime && item.closingTime);
    const respiteCare = respiteCareHours?.some((item) => item.openingTime && item.closingTime);
    const weekendCareProvidedstandardHours = standardHours
      ?.slice(5, 7)
      ?.some((item) => item.openingTime && item.closingTime);
    const weekendCareProvidedrespiteCareHours = respiteCareHours
      ?.slice(5, 7)
      ?.some((item) => item.openingTime && item.closingTime);
    const address = center.address
      ? {
          ...center.address,
          outsideUSA: center.address?.country !== 'US',
        }
      : center.address;
    const billingAddress = center.billingAddress
      ? {
          ...center.billingAddress,
          outsideUSA: center.billingAddress?.country !== 'US',
        }
      : center.billingAddress;
    dispatch(
      centerActions.setCenterSnapshot({
        ...center,
        address,
        billingAddress,
        paymentViaPaperCheck: !center.paymentViaDirectDeposit,
        chargeRegistrationFee: Boolean(center.registrationFee),
        ...credentialsData,
        experiences,
        otherExperiences,
        inServices,
        preServices,
        educations,
        otherExperiencesSelection: otherExperiences.length > 0,
        application: {
          ...center.application,
          hasPointOfContact: Boolean(center.application.pointOfContact),
        },
        standardHours,
        beforeSchoolCareHours,
        afterSchoolCareHours,
        respiteCareHours,
        beforeSchoolCare,
        afterSchoolCare,
        respiteCare,
        weekendCareProvidedstandardHours,
        weekendCareProvidedrespiteCareHours,
        ageGroups: ageGroups.map((item) => ({
          ...item,
          partTime: ['ptHourly', 'ptDaily', 'ptWeekly', 'ptMonthly', 'ptYearly'].reduce(
            (prev, curr) => prev || Boolean(item.rates[curr]),
            false,
          ),
        })),
      }),
    );
  } catch (error) {
    newrelic.noticeError(error);
    dispatch(centerActions.setCenterSnapshot(null));
  }
}

export function capitalizeWords(str) {
  return str
    .split(' ')
    .map((word) => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

export function getFullName(person) {
  return (
    [person?.firstName, person?.middleName, person?.lastName]
      .filter(Boolean)
      .map((name) => capitalizeWords(name))
      .join(' ') || ''
  );
}

export function getFirstName(person) {
  return person?.firstName ? capitalizeWords(person.firstName) : '';
}

export function isResetPasswordAllowedForUser(user) {
  if (!user || !user.userStatus) {
    return false;
  }
  return user.userStatus.toLowerCase() !== 'unconfirmed';
}

export const sentenceCase = (desc) => {
  if (!desc) return desc;
  const lower = desc.toLocaleLowerCase();
  return lower.charAt(0).toLocaleUpperCase() + lower.slice(1);
};

export function parseCareFacilityData(record) {
  const contact = [record.application?.pointOfContact?.firstName, record.application?.pointOfContact?.lastName]
    .filter((v) => v)
    .join(' ');
  return {
    ...record,
    contact,
    highPriority: record.application?.highPriority,
    submittedDate: record.application?.submittedDate ? dayjs(record.application.submittedDate) : undefined,
    familySubmittedDate: record.submittedDate ? dayjs(record.submittedDate) : undefined,
    assignedDate: record.application?.assignedDate ? dayjs(record.application.assignedDate) : undefined,
    familyAssignedDate: record.assignedDate ? dayjs(record.assignedDate) : undefined,
    assignedTo: record.application?.assignedTo,
    expiryDate: record.application?.assignedDate ? dayjs(record.application.assignedDate) : undefined,
  };
}

export function parseFamilyData(record) {
  return {
    ...record,
    submittedDate: record?.submittedDate ? dayjs(record.submittedDate) : undefined,
    assignedDate: record.assignedDate ? dayjs(record.assignedDate) : undefined,
  };
}

export function setDefaultCountry(values, addressKey) {
  return produce(values, (draft) => {
    if (draft[addressKey]) {
      const address = draft[addressKey];
      const country = address.country;
      address.country = country === 'United States' || !country ? 'US' : country;
    }
  });
}

export function communicationMapper(item) {
  const person = item.createdBy;
  const name = person ? getFullName(person) : 'System Generated';
  return {
    ...item,
    name,
  };
}

export const timeStringValidator = (value) => {
  const today = dayjs().startOf('day').format('YYYY-MM-DD');
  const isValid = dayjs(`${today}T${value?.slice(0, 5)}`, 'YYYY-MM-DDTHH:mm', true).isValid();
  return isValid;
};

export function isInViewport(selector) {
  const container = document.querySelector(selector);
  if (container) {
    const rect = container.getBoundingClientRect();
    return rect.top >= 0 && rect.left >= 0 && rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;
  }
  return false;
}
export const renderAgeRange = ({ minAge, maxAge, showFullDuration = false }) => {
  const convertAge = (age) => {
    let { years = 0, months = 0, weeks = 0 } = age || {};
    if (typeof age === 'number') {
      weeks = age;
    }
    const totalWeeks = years * 52 + months * 4 + weeks;
    if (totalWeeks < 52) {
      return {
        display: `${weeks} week${weeks !== 1 ? 's' : ''}`,
        months: months > 0 ? `${months} month${months !== 1 ? 's' : ''}` : '',
      };
    } else {
      years = Math.floor(totalWeeks / 52);
      months = Math.round((totalWeeks % 52) / 4);
      return {
        display: `${years} year${years !== 1 ? 's' : ''}`,
        months: months > 0 ? `${months} month${months !== 1 ? 's' : ''}` : '',
      };
    }
  };

  const formatAge = (age) => {
    return age.months ? `${age.display} and ${age.months}` : age.display;
  };

  const minConverted = convertAge(minAge);
  const maxConverted = convertAge(maxAge);

  return (
    <span className="!inline-block">
      {formatAge(minConverted)} - {formatAge(maxConverted)}
    </span>
  );
};

BigNumber.config({
  FORMAT: {
    decimalSeparator: '.',
    groupSeparator: ',',
    groupSize: 3,
    secondaryGroupSize: 0,
    fractionGroupSeparator: ' ',
    fractionGroupSize: 0,
  },
});

export const formatDollar = (value) => {
  const number = new BigNumber(value);
  return `$${number.toFormat(2)}`;
};

export function getProgramsColumn({ key = 'programs', width = 150, title = 'Programs' }) {
  return {
    title: (props) => <ColumnTitle {...props} title={title} dataIndex="programs" colKey="eligibility" />,
    headerText: title,
    dataIndex: 'programs',
    key,
    width,
    render: (programs) => {
      if (!programs || !programs?.length) return 'N/A';
      const types = Array.from(new Set(programs.map((program) => program.type.title))).join(', ');
      const tooltipContent = programs.map((program) => (
        <span key={program.id}>
          {program.type.title} {program.sponsor.title}
        </span>
      ));
      return <Tooltip title={<div className="flex flex-col">{tooltipContent}</div>}>{types}</Tooltip>;
    },
  };
}

export function isFamilyRole(role) {
  return [
    ROLES.FAMILY,
    ROLES.FAMILY_MANAGER,
    ROLES.FAMILY_COORDINATOR,
    ROLES.FAMILY_QC_MANAGER,
    ROLES.FAMILY_QC_COORDINATOR,
    ROLES.ER_MANAGER,
    ROLES.ER_COORDINATOR,
    ROLES.FAMILY_SUPPORT_MANAGER,
    ROLES.FAMILY_SUPPORT_SPECIALIST,
    ROLES.PARENT_SERVICES_MANAGER,
    ROLES.PARENT_SERVICES_SPECIALIST,
    ROLES.PAYMENT_MANAGER,
    ROLES.PAYMENT_COORDINATOR,
    ROLES.REPORTING_MANAGER,
    ROLES.REPORTING_ANALYST,
    ROLES.FINANCE_MANAGER,
  ].includes(role);
}

export function isFamilyInternalRole(role) {
  return role !== ROLES.FAMILY && isFamilyRole(role);
}

export function isProviderRole(role) {
  return [
    ROLES.PROVIDER,
    ROLES.PROVIDER_MANAGER,
    ROLES.PROVIDER_COORDINATOR,
    ROLES.PROVIDER_QC_MANAGER,
    ROLES.PROVIDER_QC_COORDINATOR,
    ROLES.PROVIDER_REVIEWER_MANAGER,
    ROLES.PROVIDER_REVIEWER,
    ROLES.PROVIDER_BGC_MANAGER,
    ROLES.PROVIDER_BGC_COORDINATOR,
    ROLES.PROVIDER_SUPPORT_SPECIALIST,
  ].includes(role);
}

export function isProviderInternalRole(role) {
  return role !== ROLES.PROVIDER && isProviderRole(role);
}

/**
 * Validates email addresses the same way as Ant Design's Form.Item rules
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if email is valid, false otherwise
 */
export function validateEmail(email) {
  // This is the same regular expression that Ant Design uses for email validation
  // Source: https://github.com/ant-design/ant-design/blob/master/components/_util/rules.ts
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  // Return true if the email matches the regex, false otherwise
  return emailRegex.test(email);
}

export function getOrdinalString(number) {
  const i = Math.abs(number);

  const cent = i % 100;
  if (cent >= 10 && cent <= 20) return number + 'th';

  const dec = i % 10;
  if (dec === 1) return number + 'st';
  if (dec === 2) return number + 'nd';
  if (dec === 3) return number + 'rd';

  return number + 'th';
}
